import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Play, Image, Sparkles, Loader2, Link2, Upload, Globe, ArrowRight } from 'lucide-react';

const TEMPLATES = [
  { id: 'product-reveal', label: 'Product Reveal', type: 'video' },
  { id: 'ugc-testimonial', label: 'UGC Testimonial', type: 'video' },
  { id: 'lifestyle', label: 'Lifestyle', type: 'video' },
  { id: 'hook-pattern', label: 'Hook Pattern', type: 'video' },
  { id: 'product-hero', label: 'Product Hero', type: 'image' },
  { id: 'social-post', label: 'Social Post', type: 'image' },
  { id: 'banner-set', label: 'Banner Set', type: 'image' },
  { id: 'email-hero', label: 'Email Hero', type: 'image' },
];

export default function StudioPage() {
  const { user, refreshUser } = useAuth();
  const [mode, setMode] = useState('generate'); // generate | click-to-ad
  const [genType, setGenType] = useState('image');
  const [templateId, setTemplateId] = useState('');
  const [prompt, setPrompt] = useState('');
  const [modelOverride, setModelOverride] = useState('');
  const [platform, setPlatform] = useState('');
  const [brandKitId, setBrandKitId] = useState('');
  const [brandKits, setBrandKits] = useState([]);
  const [models, setModels] = useState({ video: [], image: [] });
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [recentGens, setRecentGens] = useState([]);
  const [imageUrl, setImageUrl] = useState('');

  // Click-to-Ad state
  const [productUrl, setProductUrl] = useState('');
  const [ctaVariants, setCtaVariants] = useState(3);
  const [ctaResult, setCtaResult] = useState(null);
  const [ctaLoading, setCtaLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [bk, m, gens] = await Promise.all([api.listBrandKits(), api.getModels(), api.listGenerations({ limit: 8 })]);
        setBrandKits(bk);
        setModels(m);
        setRecentGens(gens);
      } catch {}
    };
    load();
  }, []);

  const filteredTemplates = TEMPLATES.filter(t => genType === 'video' ? t.type === 'video' : t.type === 'image');

  const handleGenerate = async () => {
    if (!prompt && !templateId) { toast.error('Enter a prompt or select a template'); return; }
    setGenerating(true);
    setResult(null);
    try {
      const gen = await api.generate({
        gen_type: genType, template_id: templateId || undefined,
        prompt: prompt || undefined, brand_kit_id: brandKitId || undefined,
        model_override: modelOverride || undefined, platform: platform || undefined,
        image_url: imageUrl || undefined,
      });
      toast.success('Generation started!');
      for (let i = 0; i < 30; i++) {
        await new Promise(r => setTimeout(r, 1000));
        const updated = await api.getGeneration(gen.id);
        if (updated.status === 'completed' || updated.status === 'failed') {
          setResult(updated);
          setRecentGens(prev => [updated, ...prev.slice(0, 7)]);
          if (updated.status === 'completed') toast.success('Generation complete!');
          else toast.error('Generation failed');
          refreshUser();
          return;
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const handleClickToAd = async () => {
    if (!productUrl) { toast.error('Enter a product URL'); return; }
    setCtaLoading(true);
    setCtaResult(null);
    try {
      const res = await api.clickToAd({
        url: productUrl, gen_type: genType, variants: ctaVariants, platforms: ['meta'],
      });
      toast.success(res.message);
      setCtaResult(res);
      // Poll for completion
      await new Promise(r => setTimeout(r, 3000));
      const gens = await api.listGenerations({ limit: ctaVariants + 2 });
      setRecentGens(gens);
      setCtaResult(prev => ({ ...prev, generations: gens.slice(0, ctaVariants) }));
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Click-to-Ad failed');
    } finally { setCtaLoading(false); }
  };

  return (
    <div className="animate-fade-in-up opacity-0" data-testid="studio-page">
      <h1 className="text-3xl font-black tracking-tight mb-6" style={{ fontFamily: 'Chivo' }}>
        Generation <span className="text-[#CDFE65]">Studio</span>
      </h1>

      {/* Mode Toggle */}
      <div className="flex gap-2 mb-6">
        <button onClick={() => setMode('generate')} className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all ${mode === 'generate' ? 'bg-[#CDFE65] text-black' : 'bg-white/5 text-white/40 hover:text-white'}`} data-testid="mode-generate">
          <Sparkles className="w-4 h-4" /> Generate
        </button>
        <button onClick={() => setMode('click-to-ad')} className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all ${mode === 'click-to-ad' ? 'bg-[#CDFE65] text-black' : 'bg-white/5 text-white/40 hover:text-white'}`} data-testid="mode-click-to-ad">
          <Globe className="w-4 h-4" /> Click-to-Ad
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Controls Panel */}
        <div className="lg:col-span-5 space-y-5" data-testid="studio-controls">
          {mode === 'click-to-ad' ? (
            /* Click-to-Ad Controls */
            <>
              <div className="bg-[#0F0F11] border border-[#CDFE65]/20 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Globe className="w-5 h-5 text-[#CDFE65]" />
                  <h3 className="font-bold">Click-to-Ad</h3>
                </div>
                <p className="text-xs text-white/40 mb-4">Paste a product URL — we'll extract images, colors, and copy to auto-generate ads.</p>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-2 block">Product URL</label>
                    <input value={productUrl} onChange={e => setProductUrl(e.target.value)} className="forge-input w-full" placeholder="https://example.com/product" data-testid="product-url-input" />
                  </div>
                  <div className="flex gap-2 p-1 bg-[#18181B] rounded-lg">
                    <button onClick={() => setGenType('image')} className={`flex-1 py-2 rounded-md text-sm font-bold ${genType === 'image' ? 'bg-[#CDFE65] text-black' : 'text-white/40'}`} data-testid="cta-type-image"><Image className="w-4 h-4 inline mr-1" /> Image</button>
                    <button onClick={() => setGenType('video')} className={`flex-1 py-2 rounded-md text-sm font-bold ${genType === 'video' ? 'bg-[#CDFE65] text-black' : 'text-white/40'}`} data-testid="cta-type-video"><Play className="w-4 h-4 inline mr-1" /> Video</button>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-2 block">Variants: {ctaVariants}</label>
                    <input type="range" min="1" max="10" value={ctaVariants} onChange={e => setCtaVariants(parseInt(e.target.value))} className="w-full" />
                  </div>
                  <button onClick={handleClickToAd} disabled={ctaLoading} className="btn-primary w-full text-base flex items-center justify-center gap-2 py-4" data-testid="click-to-ad-btn">
                    {ctaLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Generating Ads...</> : <><Globe className="w-5 h-5" /> Generate Ads from URL</>}
                  </button>
                </div>
              </div>
              {ctaResult?.scraped && (
                <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-5">
                  <h4 className="text-sm font-bold mb-2">Extracted Data</h4>
                  <p className="text-xs text-white/60 mb-1">{ctaResult.scraped.title}</p>
                  <p className="text-[10px] text-white/30 mb-3">{ctaResult.scraped.description?.slice(0, 150)}</p>
                  <div className="flex gap-1.5 mb-2">
                    {(ctaResult.scraped.colors || []).map((c, i) => (
                      <div key={i} className="w-6 h-6 rounded-full border border-white/10" style={{ background: c }} title={c} />
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(ctaResult.scraped.images || []).slice(0, 4).map((img, i) => (
                      <img key={i} src={img} alt="" className="w-12 h-12 rounded object-cover border border-white/5" loading="lazy" />
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Standard Generation Controls */
            <>
              <div className="flex gap-2 p-1 bg-[#0F0F11] rounded-lg border border-white/5">
                <button onClick={() => setGenType('image')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-bold transition-all ${genType === 'image' ? 'bg-[#CDFE65] text-black' : 'text-white/40 hover:text-white'}`} data-testid="type-image-btn">
                  <Image className="w-4 h-4" /> Image
                </button>
                <button onClick={() => setGenType('video')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-bold transition-all ${genType === 'video' ? 'bg-[#CDFE65] text-black' : 'text-white/40 hover:text-white'}`} data-testid="type-video-btn">
                  <Play className="w-4 h-4" /> Video
                </button>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-2 block">Template</label>
                <div className="grid grid-cols-2 gap-2">
                  {filteredTemplates.map(t => (
                    <button key={t.id} onClick={() => setTemplateId(templateId === t.id ? '' : t.id)} className={`text-left p-3 rounded-lg border text-sm transition-all ${templateId === t.id ? 'border-[#CDFE65] bg-[#CDFE65]/5' : 'border-white/5 bg-[#0F0F11] hover:border-white/20'}`} data-testid={`template-${t.id}`}>
                      <span className="font-medium">{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-2 block">Prompt</label>
                <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={4} className="forge-input w-full h-auto py-3 resize-none" placeholder="Describe what you want to generate..." data-testid="prompt-input" />
              </div>

              {/* Image URL for image-to-video */}
              {genType === 'video' && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-2 flex items-center gap-2"><Upload className="w-3 h-3" /> Image URL (for image-to-video)</label>
                  <input value={imageUrl} onChange={e => setImageUrl(e.target.value)} className="forge-input w-full" placeholder="https://example.com/product.jpg (optional)" data-testid="image-url-input" />
                </div>
              )}

              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-2 block">Model</label>
                <select value={modelOverride} onChange={e => setModelOverride(e.target.value)} className="forge-input w-full" data-testid="model-select">
                  <option value="">Auto (best for template)</option>
                  {(models[genType] || []).map(m => (
                    <option key={m.id} value={m.id}>{m.name} — Q{m.quality}</option>
                  ))}
                </select>
              </div>

              {brandKits.length > 0 && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-2 block">Brand Kit</label>
                  <select value={brandKitId} onChange={e => setBrandKitId(e.target.value)} className="forge-input w-full" data-testid="brandkit-select">
                    <option value="">None</option>
                    {brandKits.map(bk => <option key={bk.id} value={bk.id}>{bk.name}</option>)}
                  </select>
                </div>
              )}

              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-2 block">Platform</label>
                <select value={platform} onChange={e => setPlatform(e.target.value)} className="forge-input w-full" data-testid="platform-select">
                  <option value="">Any</option>
                  {['meta', 'tiktok', 'youtube', 'google', 'linkedin'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                </select>
              </div>

              <button onClick={handleGenerate} disabled={generating} className="btn-primary w-full text-base flex items-center justify-center gap-2 py-4" data-testid="generate-btn">
                {generating ? <><Loader2 className="w-5 h-5 animate-spin" /> Generating...</> : <><Sparkles className="w-5 h-5" /> Generate</>}
              </button>
            </>
          )}

          <p className="text-xs text-white/20 text-center font-mono">
            {user?.generations_used || 0}/{user?.generations_limit || 10} used · {user?.plan || 'free'} plan
          </p>
        </div>

        {/* Preview Panel */}
        <div className="lg:col-span-7" data-testid="studio-preview">
          <div className="bg-[#0F0F11] border border-white/5 rounded-2xl overflow-hidden min-h-[400px] flex items-center justify-center">
            {(generating || ctaLoading) ? (
              <div className="text-center p-12">
                <div className="w-16 h-16 border-2 border-[#CDFE65] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-white/40 font-mono text-sm">{mode === 'click-to-ad' ? 'Scraping URL & generating ads...' : 'Generating your creative...'}</p>
              </div>
            ) : mode === 'click-to-ad' && ctaResult?.generations?.length > 0 ? (
              <div className="w-full p-4">
                <p className="text-sm font-bold mb-3">{ctaResult.message}</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {ctaResult.generations.map(g => (
                    <div key={g.id} className="rounded-lg overflow-hidden border border-white/5">
                      {g.output_url ? (
                        <img src={g.output_url} alt="" className="w-full aspect-square object-cover" />
                      ) : (
                        <div className="w-full aspect-square bg-[#18181B] flex items-center justify-center">
                          <Loader2 className="w-6 h-6 animate-spin text-[#CDFE65]" />
                        </div>
                      )}
                      <div className="p-2 text-[10px] font-mono text-white/30">{g.template_id} · {g.model_id}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : result?.output_url ? (
              <div className="w-full">
                <img src={result.output_url} alt="Generated" className="w-full max-h-[500px] object-contain" data-testid="generation-result" />
                <div className="p-4 border-t border-white/5">
                  <div className="flex items-center gap-3 text-xs font-mono text-white/40">
                    <span className="px-2 py-0.5 bg-[#CDFE65]/10 text-[#CDFE65] rounded">{result.model_id}</span>
                    <span>{result.generation_time_ms}ms</span>
                    <span>${result.cost_usd?.toFixed(3)}</span>
                    <span className={result.status === 'completed' ? 'text-green-400' : 'text-red-400'}>{result.status}</span>
                  </div>
                  {result.enhanced_prompt && <p className="text-xs text-white/20 mt-2 line-clamp-2">{result.enhanced_prompt}</p>}
                </div>
              </div>
            ) : (
              <div className="text-center p-12">
                <Sparkles className="w-12 h-12 text-white/5 mx-auto mb-4" />
                <p className="text-white/20 text-sm">{mode === 'click-to-ad' ? 'Paste a product URL to auto-generate ads' : 'Select a template or enter a prompt to begin'}</p>
              </div>
            )}
          </div>

          {recentGens.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-mono uppercase tracking-wider text-white/30 mb-3">Recent</h3>
              <div className="grid grid-cols-4 gap-2">
                {recentGens.slice(0, 8).map(g => (
                  <div key={g.id} className="relative group rounded-lg overflow-hidden border border-white/5 card-hover cursor-pointer" onClick={() => g.output_url && setResult(g)} data-testid={`recent-gen-${g.id}`}>
                    {g.output_url ? (
                      <img src={g.output_url} alt="" className="w-full aspect-square object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full aspect-square bg-[#18181B]" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
